import React from "react";
import { Link } from "react-router-dom";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { ArrowRight, CheckCircle } from "lucide-react";

/* Testi approvati dalla committente (09/10/2026): non riscrivere o accorciare.
   Le due immagini sono schermate reali già presenti sul sito. */

const P = ({ children }) => <p className="text-[#3B4658] leading-relaxed mb-4">{children}</p>;

const PASSI = [
  ["Definiamo quali dati vuoi monitorare", "Partiamo dalle esigenze della tua impresa e dalle informazioni disponibili."],
  ["Organizziamo il cruscotto", "Impostiamo una visualizzazione chiara degli indicatori concordati."],
  ["Aggiorniamo i dati ogni mese", "Il cruscotto viene aggiornato periodicamente sulla base dei dati disponibili."],
  ["Consulti l'andamento della tua azienda", "Puoi leggere i risultati e seguire l'evoluzione degli indicatori nel tempo, secondo le funzionalità effettivamente previste dal servizio."]
];

const PannelloDirezionalePage = () => (
  <main className="pt-20" data-testid="pannello-direzionale-page">
    <PageSEO {...SEO.pannelloDirezionale} />

    <section className="py-16 md:py-24 bg-[#FAF8F5]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="text-gold font-bold tracking-widest text-sm mb-5">SERVIZIO CON AGGIORNAMENTO MENSILE</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] leading-tight mb-5">
          PANNELLO DIREZIONALE: <span className="text-gold">I NUMERI DELLA TUA AZIENDA, SEMPRE A PORTATA DI MANO.</span>
        </h1>
        <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug mb-10">
          Un cruscotto personale, aggiornato ogni mese.
        </p>
        <P>Ogni mese la tua azienda produce dati.</P>
        <P>Vendite, costi, risultati economici, movimenti finanziari e tante altre informazioni.</P>
        <P>Ma avere questi dati non significa necessariamente riuscire a consultarli in modo semplice.</P>
        <P>Spesso sono distribuiti tra bilanci, report, programmi contabili e fogli di calcolo.</P>
        <P>Il Pannello Direzionale nasce per organizzare le informazioni della tua impresa in un unico cruscotto, aggiornato mensilmente.</P>
        <P>Perché per controllare l'andamento della tua azienda non dovresti dover ricostruire ogni volta tutti i numeri.</P>
      </div>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 mt-12">
        <img
          src="/pannello/dashboard-demo.jpg"
          alt="Esempio di cruscotto del Pannello Direzionale con indicatori e grafici"
          loading="lazy"
          className="w-full rounded-lg border border-[#E8E2D5] shadow-sm"
          data-testid="pannello-immagine-dashboard"
        />
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="pannello-contenuto-cruscotto">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mb-6">Che cosa trovi nel tuo cruscotto?</h2>
        <P>Il contenuto dipende dai dati disponibili e dalle analisi concordate.</P>
        <P>Il pannello può rappresentare, secondo il servizio attivato:</P>
        <ul className="space-y-3 my-6">
          {["Ricavi e andamento del fatturato.", "Risultati economici periodici.", "Costi e loro incidenza.",
            "Margini e indicatori gestionali.", "Andamento della liquidità.", "Confronti tra periodi.",
            "Altri KPI significativi per l'attività."].map((v) => (
            <li key={v} className="flex items-start gap-3">
              <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
              <span className="text-[#3B4658] leading-relaxed">{v}</span>
            </li>
          ))}
        </ul>
        <P>Non tutti questi indicatori sono automaticamente disponibili per ogni cliente.</P>
        <P>Alcuni richiedono una contabilità gestionale o informazioni aggiuntive rispetto alla contabilità ordinaria.</P>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="pannello-come-funziona">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mb-8">Come funziona?</h2>
        <div className="space-y-8">
          {PASSI.map(([t, d], i) => (
            <div key={t} className="flex gap-5" data-testid={`pannello-passo-${i + 1}`}>
              <span className="font-heading text-3xl text-gold/70 leading-none pt-1 w-9 flex-shrink-0">{i + 1}</span>
              <div>
                <h3 className="font-heading text-xl md:text-2xl text-[#0D1527] mb-2">{t}</h3>
                <p className="text-[#3B4658] leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
        <img
          src="/pannello/obiettivi-concreti.jpg"
          alt="Schermata del Pannello Direzionale con il confronto tra obiettivi e risultati"
          loading="lazy"
          className="w-full rounded-lg border border-[#E8E2D5] shadow-sm mt-12"
          data-testid="pannello-immagine-obiettivi"
        />
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="pannello-domande">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mb-4">Devo avere già la contabilità presso il vostro studio?</h2>
        <p className="font-heading text-2xl text-gold mb-4">No.</p>
        <P>Il Pannello Direzionale può essere attivato anche da imprese che hanno già un commercialista.</P>
        <P>In questo caso verifichiamo quali informazioni possono essere messe a disposizione, con quali modalità e con quale periodicità.</P>
        <P>L'obiettivo è costruire un cruscotto attendibile e utile, non semplicemente riempire una schermata di grafici.</P>

        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mt-14 mb-4">Il Pannello Direzionale sostituisce il controllo di gestione?</h2>
        <p className="font-heading text-2xl text-gold mb-4">No.</p>
        <P>Il Pannello Direzionale organizza e visualizza le informazioni.</P>
        <P>Il controllo di gestione approfondisce i dati, analizza margini, costi, flussi e scostamenti e li utilizza per supportare le decisioni.</P>
        <P>Sono due servizi complementari, che possono essere attivati anche separatamente.</P>
        <Link
          to="/controllo-di-gestione-micro-piccole-imprese"
          className="inline-flex items-center gap-2 text-gold font-bold hover:underline mt-2"
          data-testid="pannello-link-controllo-gestione"
        >
          Scopri il controllo di gestione <ArrowRight size={16} />
        </Link>
      </div>
    </section>

    <section className="py-16 md:py-24 bg-[#0D1527]" data-testid="pannello-cta-finale">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-8">Vuoi avere il tuo cruscotto aziendale?</h2>
        <p className="text-white/75 leading-relaxed mb-4">Raccontaci quali informazioni vorresti monitorare.</p>
        <p className="text-white/75 leading-relaxed mb-10">Valuteremo insieme quali dati sono disponibili e come organizzare il tuo Pannello Direzionale.</p>
        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105"
          data-testid="pannello-cta-contatti"
        >
          RICHIEDI IL TUO PANNELLO DIREZIONALE <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </main>
);

export { PannelloDirezionalePage };
